import React, { useEffect, useMemo, useState } from "react";
import { Badge, Grid, Paper, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import DataTable from "../dataTable";
import { filterTrialBalanceRows } from "./trialBalanceUtils";

const formatAmount = (value) =>
  value == null || value === "" ? "0.00" : Number(value).toFixed(2);

const TrialBalanceView = ({
  report,
  loading = false,
  accountTypes = [],
  onFilteredRowsChange,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchText, setSearchText] = useState("");

  const columns = useMemo(
    () => [
      { field: "code", headerName: "Code", sortable: true },
      { field: "name", headerName: "accounting.journal.account", sortable: true },
      { field: "type", headerName: "Type", sortable: true },
      {
        field: "debit",
        headerName: "Debit",
        sortable: true,
        cellRender: (col, data) => formatAmount(data.debit),
      },
      {
        field: "credit",
        headerName: "Credit",
        sortable: true,
        cellRender: (col, data) => formatAmount(data.credit),
      },
      {
        field: "balance",
        headerName: "accounting.trialBalance.netBalance",
        sortable: true,
        cellRender: (col, data) => formatAmount(data.balance),
      },
    ],
    []
  );

  const filteredRows = useMemo(
    () =>
      filterTrialBalanceRows(report?.rows || [], {
        searchText,
        accountTypes,
      }),
    [report, searchText, accountTypes]
  );

  useEffect(() => {
    onFilteredRowsChange?.(filteredRows);
  }, [filteredRows, onFilteredRowsChange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [accountTypes, searchText]);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize).map((row) => ({
      ...row,
      _id: row.code,
    }));
  }, [filteredRows, currentPage, pageSize]);

  const totalDebit = Number(report?.totalDebit ?? 0);
  const totalCredit = Number(report?.totalCredit ?? 0);
  const difference = Number((totalDebit - totalCredit).toFixed(2));

  if (loading && !report) {
    return (
      <Text size="sm" color="dimmed">
        {t("accounting.trialBalance.loading")}
      </Text>
    );
  }

  if (!report) return null;

  return (
    <>
      <Paper withBorder p="md" mb="md">
        <Text size="xs" color="dimmed" mb="sm" transform="uppercase" weight={600}>
          {t("accounting.trialBalance.summaryTitle")}
        </Text>
        <Grid>
          <Grid.Col span={3}>
            <Text size="xs" color="dimmed">
              {t("Debit")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalDebit)}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" color="dimmed">
              {t("Credit")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalCredit)}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" color="dimmed">
              {t("accounting.trialBalance.difference")}
            </Text>
            <Text
              weight={700}
              size="lg"
              color={difference === 0 ? "green" : "red"}
            >
              {formatAmount(difference)}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" color="dimmed">
              {t("Status")}
            </Text>
            <Badge
              mt={4}
              size="lg"
              color={report.isBalanced ? "green" : "red"}
            >
              {report.isBalanced
                ? t("accounting.trialBalance.balanced")
                : t("accounting.trialBalance.outOfBalance")}
            </Badge>
          </Grid.Col>
        </Grid>
        <Text size="xs" color="dimmed" mt="sm">
          {t("accounting.trialBalance.netBalanceHint")}
        </Text>
      </Paper>

      {filteredRows.length === 0 ? (
        <Text size="sm" color="dimmed">
          {t("accounting.trialBalance.noActivity")}
        </Text>
      ) : (
        <DataTable
          data={pagedRows}
          columns={columns}
          hidePagination={false}
          hideSearchBar={false}
          searchText={searchText}
          onSearchChange={setSearchText}
          padding="md"
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(Number(size) || 50);
            setCurrentPage(1);
          }}
          pagination={{
            total: filteredRows.length,
            totalPage: Math.max(1, Math.ceil(filteredRows.length / pageSize)),
            page: currentPage,
          }}
        />
      )}
    </>
  );
};

export default TrialBalanceView;
